import { slugify } from '@digitalshare/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppShell } from '../components/app-shell.tsx';
import { Button, ErrorMessage, Field, Input } from '../components/ui.tsx';
import { ApiError, sectionsApi } from '../lib/api.ts';

/**
 * Secciones canónicas de la organización.
 *
 * La pantalla es deliberadamente sosa: una lista y un campo. Lo interesante no
 * ocurre aquí sino en el mapeo, que es donde cada sección se traduce a la
 * categoría real de cada periódico.
 *
 * Lo que sí importa aquí es **decir qué es una sección**, porque no es obvio:
 * mucha gente asumirá que son "las categorías" y se preguntará por qué no ve
 * las suyas.
 */
export function SectionsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['sections'],
    queryFn: sectionsApi.list,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['sections'] });
    // El mapeo depende de las secciones: al crear o borrar una, lo que la
    // pantalla de un sitio considera "pendiente" cambia.
    void queryClient.invalidateQueries({ queryKey: ['mapping'] });
  };

  const create = useMutation({
    mutationFn: () => sectionsApi.create(name.trim()),
    onSuccess: () => {
      setName('');
      invalidate();
    },
  });

  const rename = useMutation({
    mutationFn: ({ id, value }: { id: string; value: string }) => sectionsApi.rename(id, value),
    onSuccess: () => {
      setEditing(null);
      invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => sectionsApi.remove(id),
    onSuccess: invalidate,
  });

  const sections = data?.sections ?? [];
  const preview = name.trim() === '' ? '' : slugify(name);

  return (
    <AppShell title={t('sections.title')}>
      <p className="mb-6 max-w-prose text-sm text-[var(--color-muted)]">{t('sections.subtitle')}</p>

      {error instanceof ApiError ? <ErrorMessage>{error.message}</ErrorMessage> : null}
      {create.error instanceof ApiError ? <ErrorMessage>{create.error.message}</ErrorMessage> : null}
      {remove.error instanceof ApiError ? <ErrorMessage>{remove.error.message}</ErrorMessage> : null}

      <form
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate();
        }}
        className="mb-8 max-w-md rounded-xl border border-[var(--color-line)] bg-white p-5"
      >
        <Field
          id="section-name"
          label={t('sections.nameLabel')}
          // El slug acaba en URLs, así que conviene enseñarlo antes de crear.
          hint={preview === '' ? t('sections.nameHint') : t('sections.slugPreview', { slug: preview })}
        >
          <Input
            id="section-name"
            required
            placeholder={t('sections.namePlaceholder')}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>

        <Button type="submit" loading={create.isPending} disabled={name.trim().length === 0}>
          {t('sections.create')}
        </Button>
      </form>

      {isLoading ? (
        <p className="text-sm text-[var(--color-muted)]">{t('common.loading')}</p>
      ) : sections.length === 0 ? (
        <p className="rounded-xl border border-dashed border-[var(--color-line)] p-8 text-center text-sm text-[var(--color-muted)]">
          {t('sections.empty')}
        </p>
      ) : (
        <ul className="max-w-2xl space-y-2">
          {sections.map((section) => (
            <li
              key={section.id}
              className="flex items-center justify-between gap-4 rounded-lg border border-[var(--color-line)] bg-white px-4 py-3"
            >
              {editing === section.id ? (
                <form
                  className="flex flex-1 items-center gap-2"
                  onSubmit={(event) => {
                    event.preventDefault();
                    rename.mutate({ id: section.id, value: editName.trim() });
                  }}
                >
                  <input
                    // biome-ignore lint/a11y/noAutofocus: el usuario acaba de pulsar "renombrar"; el foco es lo que pidió.
                    autoFocus
                    value={editName}
                    onChange={(event) => setEditName(event.target.value)}
                    onBlur={() => setEditing(null)}
                    className="flex-1 rounded border border-[var(--color-line)] px-2 py-1 text-sm"
                  />
                  <button type="submit" className="text-sm text-[var(--color-brand)]">
                    {t('common.save')}
                  </button>
                </form>
              ) : (
                <>
                  <div className="min-w-0">
                    <span className="font-medium">{section.name}</span>
                    <code className="ml-2 text-xs text-[var(--color-muted)]">{section.slug}</code>
                  </div>

                  <div className="flex shrink-0 gap-3 text-sm">
                    <button
                      type="button"
                      onClick={() => {
                        setEditing(section.id);
                        setEditName(section.name);
                      }}
                      className="text-[var(--color-muted)] hover:underline"
                    >
                      {t('sections.rename')}
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        // El aviso dice QUE se pierde en vez de preguntar
                        // "¿estás seguro?", que no informa de nada. Rehacer el
                        // mapeo en veinte periódicos no es trivial.
                        if (window.confirm(t('sections.deleteConfirm', { name: section.name }))) {
                          remove.mutate(section.id);
                        }
                      }}
                      className="text-[var(--color-danger)] hover:underline"
                    >
                      {t('common.delete')}
                    </button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </AppShell>
  );
}
